import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import classnames from 'classnames';
import {Helmet} from 'react-helmet';
import {title} from 'lib/head';

import {Heading, Button, Text, Grid, Badge, Card, NoDataMsg} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';

import {requireSessionWithLoginPrompt} from 'lib/decorators/requireSession';
import Subheader from 'client/generic/Subheader/Subheader.react';
import sessionStore from 'client/Session/SessionStore';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {SubjectModelV2} from 'resources/GeneratedModels/Subject/SubjectModel.v2';

import lockerSVG from './locker.svg';

import './user-dashboard.scss';

const DIFFICULTIES = {
  easy: 'Easy',
  medium: 'Moderate',
  difficult: 'Difficult'
};

function getSubjectsQuery() {
  return resource('subject_v2')
    .mandarkEndpoint(['subjects_v2'])
    .meta(
      {
        context: {
          student: {
            id: sessionStore.getUserId()
          },
          subject: {
            breakdown: true
          }
        }
      },
      sessionStore.getUserId()
    )
    .filter({
      /**
       * Ensures we filter out "assessment-only" subjects
       *
       * @see https://github.com/albert-io/project-management/issues/3812
       */
      guides_v1: {
        id: {
          null: false
        }
      }
    })
    .sort('name')
    .withMeta('subject_v2')
    .pageSize('infinite');
}

function getSubjectStatsQuery(subjectId) {
  return resource('subject_v2')
    .mandarkEndpoint(['subjects_v2', subjectId])
    .customQuery({
      meta: {
        context: {
          student: {
            id: sessionStore.getUserId()
          },
          subject: {
            breakdown: true,
            counts: true
          }
        }
      }
    })
    .withMeta('subject_v2')
    .pageSize('infinite');
}

function hasAnsweredQuestions() {
  return getSubjectsQuery()
    .getResource()
    .some((subject) => subject.getTotalAnswered() > 0);
}

export default requireSessionWithLoginPrompt()(() => (
  <>
    <Helmet>{title('My Subject Analytics | Albert')}</Helmet>
    <Subheader title='My Subject Analytics' />
    <UserDashboard />
  </>
));

const UserDashboard = awaitMandarkQueries(
  () => ({
    queries: {
      subjects: getSubjectsQuery()
    }
  }),
  (props) => {
    if (!hasAnsweredQuestions()) {
      return <NoStatsSplash />;
    }
    const subjects = props.subjects
      .filter((subject) => subject.getTotalAnswered() > 0)
      .map((subject) => (
        /**
         * @todo `rowGap` doesn't seem to be working as expected...
         */
        <Grid.Row>
          <div
            className='u-mar-b_1'
            style={{
              width: '100%'
            }}
          >
            <UserDashboardItem subject={subject} key={subject.getId()} />
          </div>
        </Grid.Row>
      ));

    return (
      <Grid.Container className='u-mar-t_1'>
        <Grid.Col all={8}>{subjects}</Grid.Col>
      </Grid.Container>
    );
  }
);

class UserDashboardItem extends React.Component {
  static propTypes = {
    subject: PropTypes.instanceOf(SubjectModelV2)
  };

  constructor() {
    super();

    this.headerRef = React.createRef();
    this.bodyRef = React.createRef();

    this.state = {
      isActive: false,
      stylesBySubject: {}
    };
  }

  componentDidMount() {
    this.handleResize();
    global.addEventListener('resize', this.handleResize, false);
  }

  componentWillUnmount() {
    global.removeEventListener('resize', this.handleResize, false);
  }

  handleResize = () => {
    const elementStyles = this.getDashboardItemStyleValues();

    this.setState((state) => ({
      stylesBySubject: {
        ...state.stylesBySubject,
        ...elementStyles
      }
    }));
  };

  getDashboardItemStyleValues() {
    const subjectId = this.props.subject.getId();
    return {
      [subjectId]: {
        headerHeight: this.headerRef.current.clientHeight,
        bodyHeight: this.bodyRef.current.clientHeight
      }
    };
  }

  toggleActiveState = () => {
    this.setState((state) => ({
      isActive: !state.isActive
    }));
  };

  toggleActiveStateOnEnter = (e) => {
    if (e.which === 13) {
      // Enter key
      this.toggleActiveState();
    }
  };

  render() {
    const {subject} = this.props;
    const subjectId = subject.getId();
    const styleProperties = this.state.stylesBySubject[subjectId]
      ? this.state.stylesBySubject[subjectId]
      : {};
    const {headerHeight, bodyHeight} = styleProperties;

    const userDashboardStats = <SetOfStats subjectId={subjectId} isActive={this.state.isActive} />;

    return (
      <Card
        className='user-dashboard__item'
        shadow='default'
        style={this.state.isActive ? {paddingBottom: `${bodyHeight}px`} : {paddingBottom: '0px'}}
      >
        <div
          className='u-display_flex u-align-items_center u-justify-content_space-between'
          ref={this.headerRef}
        >
          <Heading
            size='xs'
            className='user-dashboard-item__title u-pad_2'
            onClick={this.toggleActiveState}
            onKeyPress={this.toggleActiveStateOnEnter}
            role='button'
            tabIndex='0'
          >
            {subject.getName()}
          </Heading>
          <div className='user-dashboard-item__btn-group-wrapper'>
            <div className='user-dashboard-item__btn-group u-pad_1'>
              <Button
                className='u-mar-r_1'
                variant='text'
                size='s'
                as={Link}
                to={`dashboard/${sessionStore.getUserId()}/${subjectId}`}
              >
                View Analytics
              </Button>
              <Button size='s' as={Link} to={`/${subject.getUrlSlug()}`}>
                Practice
              </Button>
            </div>
          </div>
        </div>
        <div
          className='user-dashboard-item__body'
          ref={this.bodyRef}
          style={{top: `${headerHeight}px`}}
        >
          {userDashboardStats}
        </div>
      </Card>
    );
  }
}

const SetOfStats = awaitMandarkQueries(
  (props) => {
    const options = {
      queries: {}
    };
    if (props.isActive) {
      options.queries.subject = getSubjectStatsQuery(props.subjectId);
    }
    return options;
  },
  (props) => {
    const stats = Object.keys(DIFFICULTIES).map((difficulty) => (
      <StatsItem difficulty={difficulty} key={difficulty} subject={props.subject} />
    ));
    return (
      <div
        className={classnames('user-dashboard-stats__wrapper', {
          'user-dashboard-stats__wrapper--hidden': !props.isActive
        })}
      >
        {stats}
      </div>
    );
  }
);

SetOfStats.defaultProps = {
  subject: SubjectModelV2.getDefaultModel()
};

class StatsItem extends React.Component {
  static propTypes = {
    difficulty: PropTypes.string,
    subject: PropTypes.object
  };

  formatTimeString(timeSpent) {
    let avgTime = Math.round(timeSpent / 1000);
    if (avgTime > 60) {
      const min = Math.floor(avgTime / 60);
      const sec = avgTime % 60;
      avgTime = `${min}m ${sec}s`;
    } else {
      avgTime = `${avgTime}s`;
    }
    return avgTime;
  }

  getDifficultyHeader(difficulty) {
    return DIFFICULTIES[difficulty];
  }

  render() {
    const {difficulty, subject} = this.props;
    const difficultyHeader = this.getDifficultyHeader(difficulty);
    const averageTimeSpent = this.formatTimeString(subject.getAverageTimeByDifficulty(difficulty));
    const questionsCompleted = subject.getTotalAnsweredCountByDifficulty(difficulty);
    const percentageCompleted = subject.getCompletionPercentageByDifficulty(difficulty);
    const accuracy = subject.getAverageAccuracyByDifficulty(difficulty);
    const totalPoints = subject.getTotalPointsEarnedByDifficulty(difficulty);

    return (
      <div className='user-dashboard-stats'>
        <Badge
          color={
            {
              easy: 'progress',
              medium: 'passive',
              difficult: 'danger'
            }[difficulty]
          }
        >
          {difficultyHeader}
        </Badge>
        <div className='user-dashboard-stats__stats'>
          <div className='user-dashboard-stats__stats-item'>
            <div className='user-dashboard-stats__stats-item-value'>
              <div
                className='user-dashboard-stats__stats-item-pie'
                data-percentage={percentageCompleted}
              />
              <span>{questionsCompleted}</span>
            </div>
            <Text size='xs'>Completed</Text>
          </div>
          <div className='user-dashboard-stats__stats-item'>
            <div className='user-dashboard-stats__stats-item-value'>
              <div className='user-dashboard-stats__stats-item-pie' data-percentage={accuracy} />
              <span>{accuracy}%</span>
            </div>
            <Text size='xs'>Accuracy</Text>
          </div>
          <div className='user-dashboard-stats__stats-item avg-time'>
            <div className='user-dashboard-stats__stats-item-value'>
              <div className='user-dashboard-stats__stats-item-pie' />
              <span>{averageTimeSpent}</span>
            </div>
            <Text size='xs'>Avg Time</Text>
          </div>
          <div className='user-dashboard-stats__stats-item points'>
            <div className='user-dashboard-stats__stats-item-value'>
              <div className='user-dashboard-stats__stats-item-pie' />
              <span>{totalPoints}</span>
            </div>
            <Text size='xs'>Points</Text>
          </div>
        </div>
      </div>
    );
  }
}

const NoStatsSplash = () => (
  <NoDataMsg.Container centered className='u-mar-t_7 u-mar-lr_auto'>
    <img className='user-dashboard_img--no-stats' src={lockerSVG} alt='' />
    <NoDataMsg.Heading>Welcome!</NoDataMsg.Heading>
    <NoDataMsg.Body>Get started by browsing our subject library.</NoDataMsg.Body>
    <NoDataMsg.Button color='primary' as={Link} to='/subjects'>
      Browse subject library
    </NoDataMsg.Button>
  </NoDataMsg.Container>
);
